import { useEffect, useState } from 'react';

export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  invitationCode?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    loading: true
  });

  useEffect(() => {
    checkAuthStatus();
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'token' || e.key === 'user') {
        checkAuthStatus();
      }
    };
    const onAuthChanged = () => checkAuthStatus();
    window.addEventListener('storage', onStorage);
    window.addEventListener('auth-changed', onAuthChanged as EventListener);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('auth-changed', onAuthChanged as EventListener);
    };
  }, []);

  const checkAuthStatus = () => {
    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      
      if (token && userStr) {
        const user = JSON.parse(userStr);
        setAuthState({
          isAuthenticated: true,
          user,
          token,
          loading: false
        });
      } else {
        setAuthState({
          isAuthenticated: false,
          user: null,
          token: null,
          loading: false
        });
      }
    } catch (error) {
      console.error('检查认证状态失败:', error);
      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false
      });
    }
  };

  const login = (token: string, user: User) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setAuthState({
      isAuthenticated: true,
      user,
      token,
      loading: false
    });
    // 通知其他组件刷新认证状态
    window.dispatchEvent(new Event('auth-changed'));
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
      loading: false
    });
    // 通知其他组件刷新认证状态
    window.dispatchEvent(new Event('auth-changed'));
  };

  const requireAuth = (callback: () => void) => {
    if (authState.loading) {
      return; // 等待认证状态检查完成
    }

    if (!authState.isAuthenticated) {
      // 触发登录模态框显示事件
      const event = new CustomEvent('showLoginModal', { 
        detail: { callback } 
      });
      window.dispatchEvent(event);
    } else {
      callback();
    }
  };

  const showLoginPrompt = (callback: () => void) => {
    // 触发登录模态框显示事件
    const event = new CustomEvent('showLoginModal', { 
      detail: { callback } 
    });
    window.dispatchEvent(event);
  };

  return {
    ...authState,
    login,
    logout,
    requireAuth,
    checkAuthStatus
  };
};
